export function getScaledZoneSize(zone: any) {
    const scaleX = zone?.scaleX || 1;
    const scaleY = zone?.scaleY || 1;
    return {
        width: (zone?.width || 0) * scaleX,
        height: (zone?.height || 0) * scaleY
    };
}

function rotatePoint(
    x: number,
    y: number,
    centerX: number,
    centerY: number,
    angleDegrees: number
): { x: number; y: number } {
    if (!angleDegrees) {
        return { x, y };
    }
    const angleRad = (angleDegrees * Math.PI) / 180;
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);

    const dx = x - centerX;
    const dy = y - centerY;

    const rotatedX = dx * cos - dy * sin;
    const rotatedY = dx * sin + dy * cos;

    return {
        x: rotatedX + centerX,
        y: rotatedY + centerY
    };
}

function normalizeDeg(angle: number): number {
    let a = angle % 360;
    if (a > 180) {
        a -= 360;
    }
    if (a <= -180) {
        a += 360;
    }
    return a;
}

function snapDeg(angle: number, epsilon: number = 1e-3): number {
    const a = normalizeDeg(angle);
    return Math.abs(a) < epsilon ? 0 : a;
}

export function toZoneCoordinates(x: number, y: number, zone: any): { x: number; y: number } {
    if (!zone) {
        return { x, y };
    }

    const zoneLeft = zone.left || 0;
    const zoneTop = zone.top || 0;
    const zoneAngle = zone.angle || 0;

    const rotated = rotatePoint(x, y, zoneLeft, zoneTop, -zoneAngle);

    const relativeX = rotated.x - zoneLeft;
    const relativeY = rotated.y - zoneTop;

    return {
        x: relativeX,
        y: relativeY
    };
}

export function fromZoneCoordinates(x: number, y: number, zone: any): { x: number; y: number } {
    if (!zone) {
        return { x, y };
    }

    const zoneLeft = zone.left || 0;
    const zoneTop = zone.top || 0;
    const zoneAngle = zone.angle || 0;

    const absoluteX = zoneLeft + x;
    const absoluteY = zoneTop + y;

    if (zoneAngle) {
        return rotatePoint(absoluteX, absoluteY, zoneLeft, zoneTop, zoneAngle);
    }
    return { x: absoluteX, y: absoluteY };
}

export function isObjectInZone(obj: any, zone: any): boolean {
    if (!obj || !zone) {
        return false;
    }

    const zoneAngle = zone.angle || 0;

    const rect =
        typeof obj.getBoundingRect === 'function'
            ? obj.getBoundingRect(true, true)
            : {
                  left: obj.left || 0,
                  top: obj.top || 0,
                  width: (obj.width || 0) * (obj.scaleX || 1),
                  height: (obj.height || 0) * (obj.scaleY || 1)
              };

    const objLeft = rect.left || 0;
    const objTop = rect.top || 0;
    const objWidth = rect.width || 0;
    const objHeight = rect.height || 0;

    if (!zoneAngle) {
        const { width: zoneWidth, height: zoneHeight } = getScaledZoneSize(zone);
        const zoneLeft = zone.left || 0;
        const zoneTop = zone.top || 0;

        // Intersection rectangulaire
        return (
            objLeft < zoneLeft + zoneWidth &&
            objLeft + objWidth > zoneLeft &&
            objTop < zoneTop + zoneHeight &&
            objTop + objHeight > zoneTop
        );
    }

    // Zone rotée: tester le centre du bounding rect dans le repère de la zone
    const objCenterX = objLeft + objWidth / 2;
    const objCenterY = objTop + objHeight / 2;

    const zoneCoords = toZoneCoordinates(objCenterX, objCenterY, zone);
    const zoneWidth = (zone.width || 0) * (zone.scaleX || 1);
    const zoneHeight = (zone.height || 0) * (zone.scaleY || 1);

    return zoneCoords.x >= 0 && zoneCoords.x <= zoneWidth && zoneCoords.y >= 0 && zoneCoords.y <= zoneHeight;
}

export function toZoneRelativeTransform(obj: any, zone: any) {
    if (!obj || !zone) {
        return { left: 0, top: 0, scaleX: 1, scaleY: 1, angle: 0 };
    }

    const objLeft = obj.left || 0;
    const objTop = obj.top || 0;
    const objAngle = obj.angle || 0;
    const zoneAngle = zone.angle || 0;

    const zoneCoords = toZoneCoordinates(objLeft, objTop, zone);

    const relativeAngle = snapDeg(objAngle - zoneAngle);

    return {
        left: zoneCoords.x,
        top: zoneCoords.y,
        scaleX: obj.scaleX || 1,
        scaleY: obj.scaleY || 1,
        angle: relativeAngle
    };
}

/**
 * Propriétés Fabric à appliquer au clone du clipPath sur le canvas de zone.
 * Les masques thème sont en coordonnées canvas (`absolutePositioned: true`) :
 * même repère que le main canvas → même transformation zone-relative que pour un objet.
 * Si le clip est local à l'image (`absolutePositioned: false`), retourne null : le clone garde ses coords locales.
 */
export function toZoneRelativeClipPathTransform(
    clipPath: any,
    zone: any
): Record<string, number | string | boolean> | null {
    if (!clipPath || !zone) {
        return null;
    }
    if (!clipPath.absolutePositioned) {
        return null;
    }
    const t = toZoneRelativeTransform(clipPath, zone);
    return {
        left: t.left,
        top: t.top,
        scaleX: t.scaleX,
        scaleY: t.scaleY,
        angle: t.angle || 0,
        absolutePositioned: true
    };
}
